import { useState, useCallback, useMemo } from 'react'
import type { MessageItem } from 'tripPlanner/components/Chat/MessagesList'
import type { DrawerSessionPair } from './useDrawerAskAI'

interface UseFloatingAskAISessionArgs {
  submitMessage: (message: string, options?: { messageType?: string }) => Promise<void>
  messages: Array<MessageItem>
  isStreaming: boolean
  isActive: boolean
}

interface UseFloatingAskAISessionReturn {
  handleSubmit: (message: string) => Promise<void>
  latestResponse: MessageItem | undefined
  sessionHistory: Array<DrawerSessionPair>
  isResponseStreaming: boolean
  isWaitingForResponse: boolean
  hasActiveSession: boolean
}

const NO_SESSION = {
  latestResponse: undefined,
  sessionHistory: [] as Array<DrawerSessionPair>,
  isResponseStreaming: false,
  isWaitingForResponse: false,
}

export function useFloatingAskAISession({
  submitMessage,
  messages,
  isStreaming,
  isActive,
}: UseFloatingAskAISessionArgs): UseFloatingAskAISessionReturn {
  // Only messages added after the composer became active belong to its session, so the
  // index is captured on that transition and then left alone for the rest of the session.
  const [session, setSession] = useState(() => ({ isActive, startIndex: messages.length }))

  // Re-synced during render rather than in an effect: an effect commits one render with the
  // stale index first, which paints a frame of whatever turn another surface just sent.
  if (session.isActive !== isActive) {
    setSession({ isActive, startIndex: isActive ? messages.length : session.startIndex })
  }

  const sessionStartIndex = session.startIndex

  const handleSubmit = useCallback(async(message: string) => {
    await submitMessage(message)
  }, [submitMessage])

  const { latestResponse, sessionHistory, isResponseStreaming, isWaitingForResponse } = useMemo(() => {
    // The composer stays mounted on every tab, so it re-renders on each streamed token.
    // Nothing is shown while it is inactive; skip the derivation rather than walk the
    // whole message list for a render that returns null.
    if (!isActive) return NO_SESSION

    const sessionMessages = messages.slice(sessionStartIndex)
    const pairs: Array<DrawerSessionPair> = []

    for (let i = 0; i < sessionMessages.length; i++) {
      const msg = sessionMessages[i]
      if (msg.role !== 'user') continue

      const nextMsg = sessionMessages[i + 1]
      const answer = nextMsg?.role === 'assistant' ? nextMsg : undefined
      pairs.push({ question: msg, answer })
    }

    const latestPair = pairs.length > 0 ? pairs[pairs.length - 1] : undefined
    const latestResponse = latestPair?.answer
    const lastMessage = messages[messages.length - 1]
    const isResponseStreaming = !!(isStreaming && lastMessage?.id === latestResponse?.id)
    const isWaitingForResponse = !!(
      isStreaming
      && latestPair?.answer === undefined
      && lastMessage?.id === latestPair?.question.id
    )

    return { latestResponse, sessionHistory: pairs, isResponseStreaming, isWaitingForResponse }
  }, [messages, sessionStartIndex, isStreaming, isActive])

  return {
    handleSubmit,
    latestResponse,
    sessionHistory,
    isResponseStreaming,
    isWaitingForResponse,
    hasActiveSession: sessionHistory.length > 0,
  }
}
